import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activationsApiRequest } from '@/lib/activationsApiHelpers';
import { useToast } from '@/hooks/use-toast';
import type {
  CaptureUploadUrlRequest,
  CaptureUploadUrlResponse,
  OperatorGenerateRequest,
  ActivationGenerationAdminResponse,
} from '@/types/generated/activations.generated';
import type { ActivationParticipant } from '@/types/activations';

/** Operator record-score request body — POST .../record-score. */
export interface RecordScoreRequest {
  /** Successful taps in the round; keep-highest is enforced server-side. */
  score: number;
}

/** Operator record-score response — the new submission + the resulting best score. */
export interface RecordScoreResponse {
  submissionId: string;
  bestScore: number;
}

const activationScope = (activationId: string) => ['activations', activationId] as const;
const participantsKey = (activationId: string) =>
  [...activationScope(activationId), 'participants'] as const;
const generationsKey = (activationId: string) =>
  [...activationScope(activationId), 'generations'] as const;

/**
 * Registered guests for an OperatorCurated photo booth (the operator picker source).
 * Full list — recent-first ordering is applied by the consumer.
 */
export function useActivationParticipants(activationId: string | undefined) {
  return useQuery<ActivationParticipant[]>({
    queryKey: participantsKey(activationId ?? ''),
    queryFn: () =>
      activationsApiRequest('GET', `/admin/activations/${activationId}/participants`),
    enabled: !!activationId,
  });
}

/**
 * Requests a SAS upload URL for the operator-captured photo. Returns the upload URL
 * (PUT the file there) and the resulting blob URL (passed to the generate call).
 * This is a one-shot request (not invalidating any cache), so it is a plain mutation.
 */
export function useCaptureUploadUrl(activationId: string) {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CaptureUploadUrlRequest) =>
      activationsApiRequest<CaptureUploadUrlResponse>(
        'POST',
        `/admin/activations/${activationId}/capture-upload`,
        data,
      ),
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to prepare the photo upload. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Generates N assets (one per effective prompt) for a registered guest from the captured
 * photo. 409 if the activation is not OperatorCurated. Invalidates the generations cache.
 */
export function useOperatorGenerate(activationId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({
      participantId,
      data,
    }: {
      participantId: string;
      data: OperatorGenerateRequest;
    }) =>
      activationsApiRequest<ActivationGenerationAdminResponse[]>(
        'POST',
        `/admin/activations/${activationId}/participants/${participantId}/generate`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: generationsKey(activationId) });
      queryClient.invalidateQueries({ queryKey: participantsKey(activationId) });
      toast({ title: 'Generation queued', description: 'Assets are being generated for the guest.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to start generation. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Records an operator-entered score for a registered guest of a BlazePod activation.
 * Keep-highest is enforced server-side. Invalidates the participants cache (the list
 * carries each guest's best score) and the results cache. Mirrors useOperatorGenerate.
 */
export function useRecordScore(activationId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({
      participantId,
      data,
    }: {
      participantId: string;
      data: RecordScoreRequest;
    }) =>
      activationsApiRequest<RecordScoreResponse>(
        'POST',
        `/admin/activations/${activationId}/participants/${participantId}/record-score`,
        data,
      ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: participantsKey(activationId) });
      queryClient.invalidateQueries({
        queryKey: [...activationScope(activationId), 'results'],
      });
      toast({
        title: 'Score recorded',
        description: `Best score for this guest is now ${result.bestScore}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to record the score. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/** Delivers (Send / Resend) a participant's approved asset bundle. */
export function useDeliverParticipant(activationId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (participantId: string) =>
      activationsApiRequest(
        'POST',
        `/admin/activations/${activationId}/participants/${participantId}/deliver`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: generationsKey(activationId) });
      queryClient.invalidateQueries({
        queryKey: [...activationScope(activationId), 'results'],
      });
      toast({ title: 'Delivery sent', description: 'The approved assets have been sent to the guest.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send delivery. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
